import {
  TRANSICAO_SCHEDULE,
  calcBaseAnual,
  calcImpactoCaixaFluxo,
  calcImpactoCaixaSegmentos,
  calcImpactoLiquido,
  calcRegimeAtual,
  calcRegimeFuturo,
  calcTransicaoAnual,
} from "../utils/calculoReforma";
import { marcosLegais } from "../mockDataTransicao";
import { formatMonth } from "../../creditos/dashboardData";
import { IOS } from "../../activity-logs/labels";

/**
 * Dados do dashboard da Reforma, sempre a partir do motor de cálculo das outras abas.
 *
 * O motor chama a base de "anual" e divide por 12, mas a API devolve o total do período
 * pedido (11 meses, no caso medido). Por isso os totais são sempre "do período", e os
 * segmentos voltam a ser multiplicados por 12 para desfazer o divisor. Os valores "por
 * mês" do caixa seguem o motor (total ÷ 12), para bater com a aba Caixa, e a tela diz isso.
 */

// O mesmo trio de cores de Créditos
export const SERIES = [
  { key: "vigente", label: "Regime vigente", color: IOS.blue },
  { key: "cbs", label: "CBS", color: IOS.purple },
  { key: "ibs", label: "IBS", color: IOS.green },
];

export const formatRate = (v) => `${(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
export const formatTimes = (v) => `${(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}×`;

const pct = (part, whole) => (whole ? (part / whole) * 100 : 0);

// O texto legal de 2030 afirma a virada, que aqui é calculada com os dados do projeto
const milestoneFor = (ano, turningYear) => {
  const marco = marcosLegais.find((m) => m.ano === ano);
  if (!marco) return { titulo: "", descricao: "" };
  // Mesmas alíquotas do cronograma mostrado ao lado: CBS 0,9% e IBS 0,1%
  if (ano === 2026) return { titulo: "CBS e IBS entram em vigor", descricao: "Fase de teste, com CBS de 0,9% e IBS de 0,1%, compensáveis com os tributos atuais." };
  if (marco.destaque && ano !== turningYear) {
    return { titulo: "ICMS e ISS caem para 60%", descricao: "O IBS avança e os tributos estaduais e municipais continuam a recuar." };
  }
  return marco;
};

function buildYears(transicao, regimeAtual, baseAnual) {
  const pisCofins = regimeAtual.pis.liquido + regimeAtual.cofins.liquido;
  const icmsIss = regimeAtual.icms.liquido + regimeAtual.iss.liquido;
  const turningYear = transicao.find((y) => y.futuro > y.vigente)?.ano ?? null;

  const years = transicao.map((y, i) => {
    const rule = TRANSICAO_SCHEDULE[i];
    return {
      ano: y.ano,
      label: String(y.ano),
      fase: y.fase,
      total: y.total,
      futuro: y.futuro,
      vigente: y.vigente,
      pisCofins: pisCofins * rule.pisCofPct,
      icmsIss: icmsIss * rule.icmsIssPct,
      cbs: baseAnual.margemTributavel * y.cbs,
      ibs: baseAnual.margemTributavel * y.ibs,
      rateCbs: y.cbs * 100,
      rateIbs: y.ibs * 100,
      keepsPisCofins: rule.pisCofPct * 100,
      keepsIcmsIss: rule.icmsIssPct * 100,
      delta: i === 0 ? y.total - regimeAtual.total : y.total - transicao[i - 1].total,
      fromToday: y.total - regimeAtual.total,
      ...(() => { const m = milestoneFor(y.ano, turningYear); return { marcoTitulo: m.titulo, marcoTexto: m.descricao }; })(),
    };
  });

  // 2026 é fase de teste: o salto que importa é o maior entre anos seguintes
  const biggest = years.slice(1).reduce((best, y) => (!best || y.delta > best.delta ? y : best), null);
  return { years, turningYear, biggestJump: biggest && biggest.delta > 0 ? biggest : null };
}

export function buildReformaDashboard(fiscalData, params) {
  const { totalMensal, dadosMensais = [], creditos } = fiscalData;
  const despesas = creditos?.totalCreditoConfirmado?.valorAnual || 0;

  const base = calcBaseAnual(totalMensal, despesas, params);
  const atual = calcRegimeAtual(totalMensal, base, params);
  const futuro = calcRegimeFuturo(base, params);
  const impacto = calcImpactoLiquido(atual, futuro, base);
  const fluxo = calcImpactoCaixaFluxo(totalMensal, base, futuro, params);
  const { segmentos, oficinaDetalhe } = calcImpactoCaixaSegmentos(totalMensal, base, futuro, params);
  const { years, turningYear, biggestJump } = buildYears(calcTransicaoAnual(base, atual, params), atual, base);

  const taxaPlena = futuro.cbsRate + futuro.ibsRate;
  const cash = {
    debitTodayMonth: fluxo.saidaDebito.atual,
    debitFutureMonth: fluxo.saidaDebito.futuro,
    creditFutureMonth: fluxo.entradaCredito.futuro,
    netTodayMonth: fluxo.pagamentoLiquido.atual,
    netFutureMonth: fluxo.pagamentoLiquido.futuro,
    multiplier: fluxo.saidaDebito.atual ? fluxo.saidaDebito.futuro / fluxo.saidaDebito.atual : 0,
    coverageToday: pct(fluxo.entradaCredito.atual, fluxo.saidaDebito.atual),
    coverageFuture: pct(fluxo.entradaCredito.futuro, fluxo.saidaDebito.futuro),
    onePercentOfCredits: fluxo.entradaCredito.futuro * 12 * 0.01,
  };

  const contas = (creditos?.contasElegiveis || [])
    .map((c) => ({ key: c.conta, code: c.codigo, total: c.saldo2025 || 0 }))
    .sort((a, b) => b.total - a.total);

  return {
    months: dadosMensais.map((m) => ({
      key: m.periodo,
      faturamento: m.faturamento || 0,
      carga: (m.pisLiquido || 0) + (m.cofLiquido || 0) + (m.icmsLiquido || 0),
    })),
    monthCount: dadosMensais.length,
    periodLabel: dadosMensais.length > 0
      ? `${formatMonth(dadosMensais[0].periodo)} – ${formatMonth(dadosMensais[dadosMensais.length - 1].periodo)}`
      : "",
    faturamento: base.faturamento,
    current: {
      total: atual.total,
      fromSped: atual.pis.liquido + atual.cofins.liquido + atual.icms.liquido,
      iss: atual.iss.liquido,
      rate: pct(atual.total, base.faturamento),
    },
    future: { total: futuro.total, rate: pct(futuro.total, base.faturamento) },
    impact: { value: impacto.variacaoAbsoluta, percent: impacto.variacaoPercentual * 100 },
    years,
    turningYear,
    biggestJump,
    composition: [
      { key: "icms", label: "ICMS", value: atual.icms.liquido, color: IOS.blue },
      { key: "pisCofins", label: "PIS/COFINS", value: atual.pis.liquido + atual.cofins.liquido, color: IOS.green },
      { key: "iss", label: "ISS (estimado)", value: atual.iss.liquido, color: IOS.purple },
    ].map((r) => ({ ...r, chart: Math.max(0, r.value), share: pct(r.value, atual.total) })),
    cash,
    segments: segmentos.map((s) => ({ key: s.id, label: s.nome, share: s.participacao, delta: s.deltaMensal * 12 })),
    workshop: { delta: oficinaDetalhe.aumentoAnual, rateToday: oficinaDetalhe.taxaEfetivaAtual * 100, rateFuture: oficinaDetalhe.taxaEfetivaFutura * 100 },
    credits: {
      base: base.despesasElegiveis,
      total: base.despesasElegiveis * taxaPlena,
      cbs: base.despesasElegiveis * futuro.cbsRate,
      ibs: base.despesasElegiveis * futuro.ibsRate,
      rateCbs: params.cbsPlena2033,
      rateIbs: params.ibsSpPlena2033,
      gain: impacto.creditoAdicional,
      accounts: contas,
      todayIsEstimated: creditos?.comparativoCredito?.atualDetalhado?.metodologia !== "real",
    },
    assumptions: {
      cbs: params.cbsPlena2033,
      ibs: params.ibsSpPlena2033,
      iss: params.issAtualServicos,
      costShare: pct(base.cogsTotal, base.faturamento),
      purchasesShare: pct(totalMensal.compras || 0, base.faturamento),
      onePointOfCost: base.faturamento * 0.01 * taxaPlena,
    },
  };
}
